import React, { useCallback, useContext, useMemo } from 'react';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import { Input } from 'ncoded-component-library';
import { Link, useNavigate } from 'react-router-dom';
import ReorderResizeTable from 'components/ReorderResizeTable';
import { TableContentTransformations } from 'components/ReorderResizeTable/types';
import SingleSelectDropdownFilter from 'components/SingleSelectDropdownFilter';
import RfiContext, {
  RFI_LIST_STATUS_FILTER_ALL,
} from 'router/subrouters/DataRoom/providers/Rfi/Rfi.context';
import DataRoomContext from 'router/subrouters/DataRoom/providers/DataRoom/DataRoom.context';
import { DataRoomRFI } from 'router/subrouters/DataRoom/models/DataRoomRfi';
import { getDataRoomRoute } from 'utils';
import {
  TABLE_COLUMNS,
  HEADING_TRANSLATIONS,
  SORTING_VALUES,
  CUSTOM_COL_SIZES,
  BODY_CONTENT_GENERATE,
  RfiTableRow,
} from './data';
import {
  RFI_DISPLAY_STATUS_LABEL_KEYS,
  getHasRfiNewSubmission,
  getRfiStatusCountsSummaryProps,
} from './utils';
import SearchIcon from 'icons/Search.icon';
import useQueryParams from 'hooks/useQueryParams';

import './RfiRequestsTable.styles.scss';

type RfiRequestsTableProps = {
  className?: string;
};

const RfiRequestsTable: React.FC<RfiRequestsTableProps> = (props) => {
  const { className } = props;

  const {
    isRfiListLoading,
    rfiListItems,
    rfiListTotalPages,
    rfiListTotalItems,
    rfiListStatusFilter,
    rfiListSearchInputValue,
    rfiListStatusCounts,
    rfiListStatusCountsFilter,
    updateRfiListItem,
    onRfiListSearchInputChange,
    onRfiListStatusFilterChange,
  } = useContext(RfiContext);

  const {
    dataRoom: { _id: dataRoomId },
  } = useContext(DataRoomContext);

  const { params } = useQueryParams();

  const { sort = SORTING_VALUES.displayStatus } = params;

  const navigate = useNavigate();

  const { t } = useTranslation();

  const classes = classNames('rfi-requests-table', className);

  const statusOptions = useMemo(
    () => [
      { value: RFI_LIST_STATUS_FILTER_ALL, label: t('allStatuses') },
      ...Object.entries(RFI_DISPLAY_STATUS_LABEL_KEYS).map(
        ([value, labelKey]) => ({ value, label: t(labelKey) }),
      ),
    ],
    [t],
  );

  const statusFilterTriggerTitle = useMemo(
    () =>
      statusOptions.find(({ value }) => value === rfiListStatusFilter)?.label,
    [statusOptions, rfiListStatusFilter],
  );

  const tableContentTransformations = useMemo<
    TableContentTransformations<RfiTableRow>
  >(
    () =>
      TABLE_COLUMNS.reduce(
        (acc, col) => ({
          ...acc,
          [col]: (rfi: DataRoomRFI) =>
            BODY_CONTENT_GENERATE[col]({
              rfi,
              onRfiUpdate: updateRfiListItem,
            }),
        }),
        {},
      ),
    [updateRfiListItem],
  );

  const getRowBackgroundColor = useCallback(
    (row: RfiTableRow) =>
      getHasRfiNewSubmission(row as unknown as DataRoomRFI)
        ? 'new-submission-row'
        : '',
    [],
  );

  const onRowClick = useCallback(
    (row: RfiTableRow) => {
      const { _id } = row as unknown as DataRoomRFI;

      navigate(`${getDataRoomRoute(dataRoomId, 'rfi/list')}/${_id}`);
    },
    [dataRoomId, navigate],
  );

  return (
    <div className={classes}>
      <div className="rfi-requests-table__filters">
        <Input
          placeholder={t('searchRequests')}
          prefixNode={<SearchIcon />}
          value={rfiListSearchInputValue}
          onChange={({ target: { value } }) =>
            onRfiListSearchInputChange(value)
          }
        />

        <div>
          <SingleSelectDropdownFilter
            selectedValue={rfiListStatusFilter}
            triggerTitle={statusFilterTriggerTitle}
            options={statusOptions}
            onChange={onRfiListStatusFilterChange}
          />

          <Link to="../create" className="ncoded-button ncoded-button--solid">
            {t('createFileRequest')}
          </Link>
        </div>
      </div>

      {rfiListStatusCounts && (
        <p className="rfi-requests-table__status-counts">
          {t(
            ...getRfiStatusCountsSummaryProps(
              rfiListStatusCountsFilter,
              rfiListStatusCounts,
            ),
          )}
        </p>
      )}

      <ReorderResizeTable
        isDragDisabled
        isResizeDisabled
        isDataLoading={isRfiListLoading}
        columns={TABLE_COLUMNS}
        sort={sort}
        data={rfiListItems as unknown as RfiTableRow[]}
        headingTranslations={HEADING_TRANSLATIONS}
        tableContentTransformations={tableContentTransformations}
        sortingValues={SORTING_VALUES}
        customSizes={CUSTOM_COL_SIZES}
        totalPages={rfiListTotalPages}
        totalItems={rfiListTotalItems}
        recordName="fileRequest"
        getRowBackgroundColor={getRowBackgroundColor}
        onRowClick={onRowClick}
      />
    </div>
  );
};

export default RfiRequestsTable;
